import { createContext, ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNil } from 'lodash';

import {
  maintenanceAccessCode,
  maintenanceAccessCodeKey,
  MaintenanceMode,
} from 'constants/MaintenanceConfig';
import { checkHasAccessDuringMaintenanceMode } from 'utils/maintenanceUtils';
import { logout } from 'redux/reducers/authenticationSlice';
import { useAppSelector } from 'hooks/useAppSelector';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useDidUpdate } from 'rooks';

const getMaintenanceModeState = () =>
  MaintenanceMode ? !checkHasAccessDuringMaintenanceMode() : false;

const initialMaintenanceModeState = getMaintenanceModeState();

export const MaintenanceModeContext = createContext({
  isMaintenanceMode: initialMaintenanceModeState,
  setIsMaintenanceMode: (isMaintenanceMode: boolean) => {},
});

type Props = {
  children: ReactNode;
};

export default function MaintenanceModeProvider(props: Props) {
  const { children } = props;
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(
    initialMaintenanceModeState,
  );
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (location.pathname !== `/maintenance-access/${maintenanceAccessCode}`) {
      setIsMaintenanceMode(getMaintenanceModeState());
    }
    // eslint-disable-next-line
  }, []);

  useDidUpdate(() => {
    if (MaintenanceMode && !isMaintenanceMode) {
      localStorage.setItem(maintenanceAccessCodeKey, maintenanceAccessCode);
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line
  }, [isMaintenanceMode]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        MaintenanceMode &&
        (event.key === maintenanceAccessCodeKey || isNil(event.key))
      ) {
        setIsMaintenanceMode(getMaintenanceModeState());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isMaintenanceMode && user) {
      dispatch(logout());
    }
  }, [user, isMaintenanceMode, dispatch]);

  return (
    <MaintenanceModeContext.Provider
      value={{ isMaintenanceMode, setIsMaintenanceMode }}
    >
      {children}
    </MaintenanceModeContext.Provider>
  );
}
